import { createElement } from 'react'
import { useQueryClient, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { bookmarkApi } from '../../../shared/services/bookmarkApi'
import UndoToast from '../components/bookmark/UndoToast'
import { buildUndoRequests, type BulkSnapshotItem } from './bulkUndo'
import type {
  BookmarkListResponse,
  BookmarkUrl,
  BulkActionRequest,
} from '../../../types/bookmark'

/**
 * 북마크 변경 작업.
 *
 * - 즐겨찾기·삭제는 낙관적 업데이트로 즉시 반영하고 실패 시 되돌린다.
 * - 오류는 toast 로 사용자에게 알린다 (기존 alert() 대체).
 */

export const BOOKMARK_LIST_KEY = ['bookmarked-urls'] as const

/** 되돌리기 버튼을 띄워 두는 시간 */
const UNDO_WINDOW_MS = 6000

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.length > 0) {
      return message
    }
  }
  return fallback
}

/** 서버가 일부 필드만 돌려줄 수 있어 기존 값 위에 덮어쓴다 */
function mergeUrl(
  current: BookmarkUrl,
  patch: Partial<BookmarkUrl> | undefined
): BookmarkUrl {
  if (!patch) {
    return current
  }
  return { ...current, ...patch, _id: current._id }
}

export interface UseBookmarkMutationsOptions {
  /** 폴더 목록·태그 집계처럼 목록 밖 데이터도 갱신해야 할 때 */
  onSideEffect?: () => void
}

export function useBookmarkMutations(
  options: UseBookmarkMutationsOptions = {}
) {
  const queryClient = useQueryClient()
  const { onSideEffect } = options

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: BOOKMARK_LIST_KEY })
    queryClient.invalidateQueries({ queryKey: ['tags'] })
    onSideEffect?.()
  }

  /** 현재 캐시된 모든 목록 페이지에 변형을 적용하고 이전 스냅샷을 돌려준다 */
  const patchCachedLists = (
    transform: (list: BookmarkListResponse) => BookmarkListResponse
  ) => {
    const snapshot = queryClient.getQueriesData<BookmarkListResponse>({
      queryKey: BOOKMARK_LIST_KEY,
    })
    snapshot.forEach(([key, value]) => {
      if (value) {
        queryClient.setQueryData(key, transform(value))
      }
    })
    return snapshot
  }

  const restore = (
    snapshot: ReturnType<typeof patchCachedLists> | undefined
  ) => {
    snapshot?.forEach(([key, value]) => queryClient.setQueryData(key, value))
  }

  const toggleFavorite = useMutation({
    mutationFn: (urlId: string) => bookmarkApi.toggleFavorite(urlId),
    onMutate: async (urlId: string) => {
      await queryClient.cancelQueries({ queryKey: BOOKMARK_LIST_KEY })
      const snapshot = patchCachedLists(list => ({
        ...list,
        urls: list.urls.map(url =>
          url._id === urlId ? { ...url, isFavorite: !url.isFavorite } : url
        ),
      }))
      return { snapshot }
    },
    onError: (error, _urlId, context) => {
      restore(context?.snapshot)
      toast.error(errorMessage(error, '즐겨찾기 변경에 실패했습니다.'))
    },
    onSettled: invalidateAll,
  })

  const remove = useMutation({
    mutationFn: (urlId: string) => bookmarkApi.remove(urlId),
    onMutate: async (urlId: string) => {
      await queryClient.cancelQueries({ queryKey: BOOKMARK_LIST_KEY })
      const snapshot = patchCachedLists(list => ({
        ...list,
        urls: list.urls.filter(url => url._id !== urlId),
        total: Math.max(0, list.total - 1),
      }))
      return { snapshot }
    },
    onError: (error, _urlId, context) => {
      restore(context?.snapshot)
      toast.error(errorMessage(error, '링크 삭제에 실패했습니다.'))
    },
    onSuccess: () => toast.success('링크를 삭제했습니다.'),
    onSettled: invalidateAll,
  })

  const moveToFolder = useMutation({
    mutationFn: ({
      urlId,
      folderId,
    }: {
      urlId: string
      folderId: string | null
    }) => bookmarkApi.moveToFolder(urlId, folderId),
    onError: error =>
      toast.error(errorMessage(error, '폴더 이동에 실패했습니다.')),
    onSuccess: () => toast.success('링크를 이동했습니다.'),
    onSettled: invalidateAll,
  })

  /**
   * 링크 수정 (제목·태그·폴더·비밀번호·만료일).
   * 서버는 success:false 로도 실패를 알리므로 거부로 바꿔 한 곳에서 처리한다.
   */
  const update = useMutation({
    mutationFn: async ({
      urlId,
      input,
    }: {
      urlId: string
      input: Record<string, unknown>
    }) => {
      const result = await bookmarkApi.update(urlId, input)
      if (!result.success) {
        throw new Error(result.message || '링크 수정에 실패했습니다.')
      }
      return { urlId, url: result.data?.url }
    },
    onSuccess: ({ urlId, url }) => {
      // 서버가 돌려준 값으로 목록을 먼저 맞춰 둔다. 무효화 응답을
      // 기다리는 동안 패널과 카드가 엇갈려 보이지 않게 한다.
      patchCachedLists(list => ({
        ...list,
        urls: list.urls.map(item =>
          item._id === urlId ? mergeUrl(item, url) : item
        ),
      }))
      toast.success('저장했습니다.')
    },
    onError: error =>
      toast.error(errorMessage(error, '링크 수정에 실패했습니다.')),
    onSettled: invalidateAll,
  })

  /** 되돌리기용으로 작업 직전의 값을 모아 둔다 */
  const snapshotItems = (urlIds: string[]): BulkSnapshotItem[] => {
    const seen = new Map<string, BulkSnapshotItem>()
    queryClient
      .getQueriesData<BookmarkListResponse>({ queryKey: BOOKMARK_LIST_KEY })
      .forEach(([, list]) => {
        list?.urls.forEach(url => {
          if (urlIds.includes(url._id) && !seen.has(url._id)) {
            seen.set(url._id, {
              _id: url._id,
              folderId: url.folderId?._id ?? null,
              isFavorite: url.isFavorite,
            })
          }
        })
      })
    // 요청한 순서를 지켜야 되돌린 결과가 눈에 띄게 뒤섞이지 않는다
    return urlIds.map(id => seen.get(id)).filter(Boolean) as BulkSnapshotItem[]
  }

  const bulk = useMutation({
    mutationFn: (request: BulkActionRequest) => bookmarkApi.bulk(request),
    onMutate: (request: BulkActionRequest) => ({
      undoRequests: buildUndoRequests(
        request.action,
        snapshotItems(request.urlIds)
      ),
    }),
    onError: error =>
      toast.error(errorMessage(error, '일괄 작업에 실패했습니다.')),
    onSuccess: (result, _request, context) => {
      const undoRequests = context?.undoRequests ?? []
      if (undoRequests.length === 0) {
        toast.success(result.message)
        return
      }
      // 컴포넌트로 넘겨야 UndoToast 안의 훅이 자기 렌더에서 돈다.
      // 함수로 바로 호출하면 Toaster 의 훅 목록에 끼어든다.
      toast.custom(
        item =>
          createElement(UndoToast, {
            message: result.message,
            isVisible: item.visible,
            onUndo: () => {
              toast.dismiss(item.id)
              undoRequests.forEach(request => bulk.mutate(request))
            },
          }),
        { duration: UNDO_WINDOW_MS }
      )
    },
    onSettled: invalidateAll,
  })

  const deleteTag = useMutation({
    mutationFn: (tagId: string) => bookmarkApi.deleteTag(tagId),
    onError: error =>
      toast.error(errorMessage(error, '태그 삭제에 실패했습니다.')),
    onSuccess: () => toast.success('태그를 삭제했습니다.'),
    onSettled: invalidateAll,
  })

  return { toggleFavorite, remove, update, moveToFolder, bulk, deleteTag }
}

export default useBookmarkMutations
